import { DEFAULT_SETTINGS, STORAGE } from '../constants'
import type { MetaPreview, Settings } from '../types'
import { getLibrary, getProgress, getWatched, replaceLibrary, replaceProgress, replaceWatched, saveSettings } from './storage'
import { isSyncSnapshot } from './remoteProtocol'
import type { SyncSnapshot } from './syncTypes'

export type { SyncSnapshot } from './syncTypes'

/** Leave ample room in the 256 KiB relay frame for live player/settings state. */
export const REMOTE_SYNC_MAX_BYTES = 96 * 1024
export const SYNC_FILE_MAX_BYTES = 512 * 1024
const PHONE_SYNC_STORAGE_MAX_BYTES = 12 * (REMOTE_SYNC_MAX_BYTES + 1024)

export interface PhoneSyncCarrier {
  deviceId: string
  deviceName: string
  capturedAt: number
  snapshot: SyncSnapshot
}

function compactPreferences(settings: Settings): SyncSnapshot['preferences'] {
  const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T) => (
    typeof value === 'string' && allowed.includes(value as T) ? value as T : fallback
  )
  return {
    subtitleLanguage: String(settings.subtitleLanguage || DEFAULT_SETTINGS.subtitleLanguage).slice(0, 12),
    subtitleSize: oneOf(settings.subtitleSize, ['small', 'medium', 'large'] as const, DEFAULT_SETTINGS.subtitleSize),
    subtitleDelay: Number.isFinite(settings.subtitleDelay) ? Math.max(-30, Math.min(30, settings.subtitleDelay)) : 0,
    subtitlePosition: oneOf(settings.subtitlePosition, ['bottom', 'middle', 'top'] as const, DEFAULT_SETTINGS.subtitlePosition),
    subtitleBackground: oneOf(settings.subtitleBackground, ['none', 'shadow', 'box'] as const, DEFAULT_SETTINGS.subtitleBackground),
    subtitleSdh: oneOf(settings.subtitleSdh, ['include', 'prefer', 'exclude'] as const, DEFAULT_SETTINGS.subtitleSdh),
    subtitleForced: oneOf(settings.subtitleForced, ['include', 'prefer', 'only'] as const, DEFAULT_SETTINGS.subtitleForced),
    subtitleEncoding: oneOf(settings.subtitleEncoding, ['auto', 'utf-8', 'windows-1252'] as const, DEFAULT_SETTINGS.subtitleEncoding),
    preferredAudioLanguage: String(settings.preferredAudioLanguage || DEFAULT_SETTINGS.preferredAudioLanguage).slice(0, 12),
    hdrMode: oneOf(settings.hdrMode, ['auto', 'prefer', 'sdr'] as const, DEFAULT_SETTINGS.hdrMode),
    autoplayNext: settings.autoplayNext === true,
  }
}

function compactProgress(entry: ReturnType<typeof getProgress>[number]) {
  return {
    key: String(entry.key).slice(0, 300),
    meta: compactMeta(entry.meta),
    video: entry.video ? {
      id: String(entry.video.id).slice(0, 240),
      title: entry.video.title?.slice(0, 200),
      name: entry.video.name?.slice(0, 200),
      season: Number.isInteger(entry.video.season) ? entry.video.season : undefined,
      episode: Number.isInteger(entry.video.episode) ? entry.video.episode : undefined,
      released: entry.video.released?.slice(0, 80),
    } : undefined,
    currentTime: Number.isFinite(entry.currentTime) ? Math.max(0, Math.min(86_400, entry.currentTime)) : 0,
    duration: Number.isFinite(entry.duration) ? Math.max(0, Math.min(86_400, entry.duration)) : 0,
    updatedAt: Number.isFinite(entry.updatedAt) ? Math.max(0, Math.min(9_999_999_999_999, entry.updatedAt)) : 0,
  }
}

function newestWatched(maximum = 300) {
  return Object.fromEntries(Object.entries(getWatched())
    .filter(([key, timestamp]) => key && key.length <= 300 && Number.isFinite(timestamp) && timestamp > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maximum))
}

function fitPortableSnapshot(snapshot: SyncSnapshot) {
  let result = snapshot
  if (encodedBytes(result) <= SYNC_FILE_MAX_BYTES) return result
  result = {
    ...result,
    library: result.library.map((item) => withoutHeavyMeta(item)),
    progress: result.progress.map((entry) => ({ ...entry, meta: withoutHeavyMeta(entry.meta, false) })),
  }
  if (encodedBytes(result) <= SYNC_FILE_MAX_BYTES) return result
  result = { ...result, library: result.library.map((item, index) => withoutHeavyMeta(item, index < 24)) }
  while (encodedBytes(result) > SYNC_FILE_MAX_BYTES && Object.keys(result.watched).length) {
    result = { ...result, watched: Object.fromEntries(Object.entries(result.watched).slice(0, -25)) }
  }
  while (encodedBytes(result) > SYNC_FILE_MAX_BYTES && (result.library.length || result.progress.length)) {
    result = {
      ...result,
      library: result.library.slice(0, Math.max(0, result.library.length - 5)),
      progress: result.progress.slice(0, Math.max(0, result.progress.length - 5)),
    }
  }
  return result
}

export function createSyncSnapshot(settings: Settings): SyncSnapshot {
  return fitPortableSnapshot({
    version: 1,
    exportedAt: Date.now(),
    library: getLibrary().slice(0, 100).map(compactMeta),
    progress: getProgress().slice(0, 30).map(compactProgress),
    watched: newestWatched(),
    preferences: compactPreferences(settings),
  })
}

function compactMeta(item: MetaPreview): MetaPreview {
  return {
    id: String(item.id).slice(0, 240),
    type: item.type,
    name: String(item.name).slice(0, 200),
    poster: item.poster?.slice(0, 800),
    background: item.background?.slice(0, 800),
    description: item.description?.slice(0, 800),
    releaseInfo: item.releaseInfo?.slice(0, 80),
    year: typeof item.year === 'number' ? item.year : item.year?.slice(0, 20),
    imdbRating: item.imdbRating?.slice(0, 20),
    genre: item.genre?.slice(0, 12).map((value) => value.slice(0, 40)),
    genres: item.genres?.slice(0, 12).map((value) => value.slice(0, 40)),
    addonUrl: item.addonUrl?.slice(0, 500),
  }
}

function encodedBytes(value: unknown) {
  const raw = typeof value === 'string' ? value : JSON.stringify(value)
  return new TextEncoder().encode(raw).byteLength
}

function withoutHeavyMeta(item: MetaPreview, poster = true): MetaPreview {
  return {
    id: item.id,
    type: item.type,
    name: item.name,
    poster: poster ? item.poster : undefined,
    releaseInfo: item.releaseInfo,
    year: item.year,
    imdbRating: item.imdbRating,
    addonUrl: item.addonUrl,
  }
}

/** Private paired-phone state is intentionally smaller than an export file so
 * it stays bounded inside one authenticated WebSocket message. */
export function createRemoteSyncSnapshot(settings: Settings): SyncSnapshot {
  const snapshot = createSyncSnapshot(settings)
  const watchedEntries = Object.entries(snapshot.watched)
  let result: SyncSnapshot = {
    ...snapshot,
    library: snapshot.library.slice(0, 60).map(compactMeta),
    progress: snapshot.progress.slice(0, 30).map(compactProgress),
    watched: Object.fromEntries(watchedEntries),
  }

  if (encodedBytes(result) <= REMOTE_SYNC_MAX_BYTES) return result

  // First discard presentation-only fields; IDs, names and provider origins
  // are sufficient for the target TV to resolve full metadata again.
  result = {
    ...result,
    library: result.library.map((item) => withoutHeavyMeta(item)),
    progress: result.progress.map((entry) => ({ ...entry, meta: withoutHeavyMeta(entry.meta, false) })),
  }
  if (encodedBytes(result) <= REMOTE_SYNC_MAX_BYTES) return result

  result = { ...result, library: result.library.map((item, index) => withoutHeavyMeta(item, index < 12)) }
  if (encodedBytes(result) <= REMOTE_SYNC_MAX_BYTES) return result

  // Corrupt or adversarial localStorage can contain maximum-width Unicode in
  // every watched key. Retain newest records while enforcing the wire budget.
  let retainedWatched = Object.entries(result.watched)
  while (encodedBytes(result) > REMOTE_SYNC_MAX_BYTES && retainedWatched.length) {
    retainedWatched = retainedWatched.slice(0, Math.max(0, retainedWatched.length - 25))
    result = { ...result, watched: Object.fromEntries(retainedWatched) }
  }
  while (encodedBytes(result) > REMOTE_SYNC_MAX_BYTES && (result.library.length || result.progress.length)) {
    result = {
      ...result,
      library: result.library.slice(0, Math.max(0, result.library.length - 5)),
      progress: result.progress.slice(0, Math.max(0, result.progress.length - 5)),
    }
  }
  return result
}

export function getPhoneSyncCarriers(): PhoneSyncCarrier[] {
  try {
    const raw = localStorage.getItem(STORAGE.phoneSyncCarriers) || '[]'
    if (encodedBytes(raw) > PHONE_SYNC_STORAGE_MAX_BYTES) return []
    const value = JSON.parse(raw) as PhoneSyncCarrier[]
    if (!Array.isArray(value)) return []
    const seen = new Set<string>()
    return value
      .filter((entry) => {
        if (!entry || typeof entry.deviceId !== 'string' || !entry.deviceId || entry.deviceId.length > 80
          || typeof entry.deviceName !== 'string' || entry.deviceName.length > 80
          || !Number.isFinite(entry.capturedAt) || entry.capturedAt <= 0
          || !isSyncSnapshot(entry.snapshot, 60) || encodedBytes(entry.snapshot) > REMOTE_SYNC_MAX_BYTES
          || seen.has(entry.deviceId)) return false
        seen.add(entry.deviceId)
        return true
      })
      .sort((a, b) => b.capturedAt - a.capturedAt)
      .slice(0, 12)
  } catch { return [] }
}

export function savePhoneSyncCarrier(deviceId: string, deviceName: string, snapshot: SyncSnapshot) {
  if (!deviceId || deviceId.length > 80 || !isSyncSnapshot(snapshot, 60) || encodedBytes(snapshot) > REMOTE_SYNC_MAX_BYTES) return getPhoneSyncCarriers()
  const next = [{
    deviceId: deviceId.slice(0, 80),
    deviceName: (deviceName || 'Vela TV').slice(0, 80),
    capturedAt: Date.now(),
    snapshot,
  }, ...getPhoneSyncCarriers().filter((entry) => entry.deviceId !== deviceId)].slice(0, 12)
  try { localStorage.setItem(STORAGE.phoneSyncCarriers, JSON.stringify(next)) } catch { /* phone storage full */ }
  return next
}

export function applySyncSnapshot(raw: string, settings: Settings, mode: 'merge' | 'replace' = 'merge') {
  if (raw.length > SYNC_FILE_MAX_BYTES || encodedBytes(raw) > SYNC_FILE_MAX_BYTES) throw new Error('Vela sync files must be 512 KiB or smaller.')
  const snapshot = JSON.parse(raw) as unknown
  if (!isSyncSnapshot(snapshot, 100)) throw new Error('This is not a valid bounded Vela sync file.')
  if (mode !== 'merge' && mode !== 'replace') throw new Error('Choose merge or replace for this sync file.')
  const library = replaceLibrary(mode === 'replace' ? snapshot.library : [...getLibrary(), ...snapshot.library])
  const progress = replaceProgress(mode === 'replace' ? snapshot.progress : [...getProgress(), ...snapshot.progress])
  const currentWatched = getWatched()
  const watched = replaceWatched(mode === 'replace' ? snapshot.watched : Object.fromEntries(
    Array.from(new Set([...Object.keys(currentWatched), ...Object.keys(snapshot.watched)]))
      .map((key) => [key, Math.max(currentWatched[key] || 0, snapshot.watched[key] || 0)]),
  ))
  const allowed = [
    'subtitleLanguage', 'subtitleSize', 'subtitleDelay', 'subtitlePosition', 'subtitleBackground',
    'subtitleSdh', 'subtitleForced', 'subtitleEncoding', 'preferredAudioLanguage', 'hdrMode', 'autoplayNext',
  ] as const
  const preferences = Object.fromEntries(allowed.map((key) => [key, snapshot.preferences[key]])) as Partial<Settings>
  const next = { ...settings, ...preferences }
  saveSettings(next)
  return { settings: next, library, progress, watched }
}

/**
 * Returns whether the browser accepted the download. An installed webOS app
 * runs under file:// with no download manager, where this cannot work — the
 * paired-phone carrier is the path that does.
 */
export function downloadSyncSnapshot(settings: Settings) {
  try {
    const link = document.createElement('a')
    if (typeof link.download !== 'string') return false
    const url = URL.createObjectURL(new Blob([JSON.stringify(createSyncSnapshot(settings), null, 2)], { type: 'application/json' }))
    link.href = url
    link.download = `vela-sync-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
    return true
  } catch {
    return false
  }
}
